import { faChartLine, faLeaf, faLeafHeart } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { DatePicker } from "@mui/x-date-pickers"
import { Box, Container, Grid, TextField, Typography } from "@mui/material"
import { useApiGet } from "auth/useApi2"
import ClpTable from "components/ClpTable"
import { GlobalLoaderContext } from "components/GlobalLoadingProvider"
import PaperBox from "components/PaperBox"
import { apiEndpoints } from "generated/apiEndpoints"
import moment from "moment"
import { StatsCard } from "pages/planning/components/StatsCard"
import { StatsCardLoading } from "pages/planning/components/StatsCardLoading"
import { useContext, useEffect, useMemo, useState } from "react"
import {
  StyledErrorUserAvatar,
  StyledPrimaryUserAvatar,
  StyledSuccessUserAvatar,
  StyledTableContainer
} from "shared/styled/Global"
import { useTranslation } from "react-i18next"

export const Percentage = (props) => {
  const { value, maxDecimals } = props
  var percentageValue = value * 100

  if (maxDecimals !== undefined) {
    percentageValue = percentageValue && parseFloat(percentageValue.toFixed(maxDecimals))
  }

  return <span>{percentageValue} %</span>
}

const Date = ({ date, setDate }) => {
  function disableDays(date) {
    return date > moment()
  }

  return (
    <DatePicker
      value={date ? moment(date) : null}
      shouldDisableDate={disableDays}
      onChange={(newValue) => setDate(moment(newValue).format("YYYY-MM-DD"))}
      renderInput={(params) => <TextField {...params} />}
    />
  )
}

const explain_fuel_consumption = `Here you can see an overview of how your fuel consumption compares to your colleagues'.
Each trip you drive is included in the comparison, and you can view the results as percentages (%) per day, week, and month.`

const EcoDriving = () => {
  const loadingContext = useContext(GlobalLoaderContext)
  const [ecodrivingResult, getEcoDriving] = useApiGet()
  const [ecodrivingDetailsResult, getEcoDrivingDetails] = useApiGet()
  const [date, setDate] = useState(moment())
  const { t } = useTranslation()

  useEffect(() => {
    getEcoDriving(apiEndpoints.planning.getecodriving)
  }, [getEcoDriving])

  useEffect(() => {
    getEcoDrivingDetails(
      apiEndpoints.planning.getdetailedecodriving.replace("{date}", moment(date).format("YYYY-MM-DD"))
    )
  }, [getEcoDrivingDetails])

  useEffect(() => {
    getEcoDrivingDetails(
      apiEndpoints.planning.getdetailedecodriving.replace("{date}", moment(date).format("YYYY-MM-DD"))
    )
  }, [date])

  useEffect(() => {
    loadingContext.setLoading(() => ecodrivingResult.status === 1)
  })
  function LeafPercentageIcon(props) {
    const icon =
      props.value < 0.95 ? (
        <StyledSuccessUserAvatar aria-label="icon">
          <FontAwesomeIcon icon={faLeafHeart} />
        </StyledSuccessUserAvatar>
      ) : props.value >= 0.95 && props.value <= 1.05 ? (
        <StyledPrimaryUserAvatar aria-label="icon">
          <FontAwesomeIcon icon={faLeaf} />
        </StyledPrimaryUserAvatar>
      ) : props.value > 1.05 ? (
        <StyledErrorUserAvatar aria-label="icon">
          <FontAwesomeIcon icon={faLeaf} />
        </StyledErrorUserAvatar>
      ) : null

    return icon
  }

  function percentageLabel(value) {
    const icon =
      value < 0.989
        ? t("planning.ecodriving_less_than_colleagues", "You consume less than your colleagues")
        : value >= 0.989 && value <= 1.01
        ? t("planning.ecodriving_equal_to_colleagues", "You consume about the same as your colleagues on average")
        : value > 1.01
        ? t("planning.ecodriving_more_than_colleagues", "You consume more than your colleagues")
        : null

    return icon
  }

  const columns = useMemo(
    () => [
      {
        accessorKey: "ecoTurId",
        header: t("planning.route", "Route")
      },
      {
        accessorKey: "turbeskrivning",
        header: t("planning.service", "Service")
      },
      {
        accessorKey: "fordonRegistreringsnummer",
        header: t("settings.user.bus", "Bus")
      },
      {
        accessorFn: (row) => {
          return row.ecoIndex && parseFloat(row.ecoIndex.toFixed(2))
        },
        header: t("planning.comparsison_index", "Comparison index")
      },
      {
        accessorFn: (row) => {
          return row.distance && parseFloat(row.distance.toFixed(2))
        },
        header: t("planning.distance", "Distance") + " (KM)"
      },
      {
        accessorFn: (row) => {
          return row.outsideTemp && parseFloat(row.outsideTemp.toFixed(2))
        },
        header: t("planning.average_temperature", "Average temperature") + " (C)"
      },
      {
        accessorFn: (row) => {
          return row.avgFuelUsed && parseFloat(row.avgFuelUsed.toFixed(2))
        },
        header: t("planning.average_consumption", "Average consumption") + " (l/Mil)"
      }
    ],
    []
  )

  return (
    <div>
      <Container>
        <Box mb={4}>
          <Grid container direction="row" justifyContent="space-between" alignItems="center">
            <Grid item md={8}>
              <Typography variant="h1" gutterBottom>
                {t("planning.economical_driving", "Economical driving")}
              </Typography>
              <Typography variant="paragraphLarge" gutterBottom>
                {t("planning.explain_fuel_consumption", explain_fuel_consumption)}
              </Typography>
            </Grid>
          </Grid>
        </Box>
        <Grid container direction="row" spacing={3}>
          <Grid item sm={3}>
            <PaperBox>
              {ecodrivingResult.status === 2 ? (
                <StatsCard
                  bigIcon={
                    ecodrivingResult.status === 2 ? (
                      <LeafPercentageIcon value={ecodrivingResult.data.ecoIndexDay} />
                    ) : null
                  }
                  statsText={t("planning.my_day", "My day")}
                  statsDesc={t("planning.previous_workday", "Previous workday")}
                  statsValue={
                    ecodrivingResult.status === 2 ? (
                      <Percentage value={ecodrivingResult.data.ecoIndexDay} maxDecimals={1} />
                    ) : (
                      "-"
                    )
                  }
                  statsIconText={ecodrivingResult.status === 2 && ecodrivingResult.data?.dayLabel}
                  subText={percentageLabel(ecodrivingResult.data.ecoIndexDay)}
                />
              ) : (
                <StatsCardLoading />
              )}
            </PaperBox>
          </Grid>
          <Grid item sm={3}>
            <PaperBox>
              {ecodrivingResult.status === 2 ? (
                <StatsCard
                  bigIcon={
                    ecodrivingResult.status === 2 ? (
                      <LeafPercentageIcon value={ecodrivingResult.data.ecoIndexWeek} />
                    ) : null
                  }
                  statsText={t("planning.my_week", "My week")}
                  statsDesc={t("planning.past_seven_days", "Past 7 days")}
                  statsValue={
                    ecodrivingResult.status === 2 ? (
                      <Percentage value={ecodrivingResult.data.ecoIndexWeek} maxDecimals={1} />
                    ) : (
                      "-"
                    )
                  }
                  statsIconText={ecodrivingResult.status === 2 && ecodrivingResult.data?.weekLabel}
                  subText={percentageLabel(ecodrivingResult.data.ecoIndexWeek)}
                />
              ) : (
                <StatsCardLoading />
              )}
            </PaperBox>
          </Grid>
          <Grid item sm={3}>
            <PaperBox>
              {ecodrivingResult.status === 2 ? (
                <StatsCard
                  bigIcon={
                    ecodrivingResult.status === 2 ? (
                      <LeafPercentageIcon value={ecodrivingResult.data.ecoIndexMonth} />
                    ) : null
                  }
                  statsText={t("planning.my_month", "My month")}
                  statsDesc={t("planning.past_thirty_days", "Past 30 days")}
                  statsValue={
                    ecodrivingResult.status === 2 ? (
                      <Percentage value={ecodrivingResult.data.ecoIndexMonth} maxDecimals={1} />
                    ) : (
                      "-"
                    )
                  }
                  statsIconText={ecodrivingResult.status === 2 && ecodrivingResult.data?.weekLabel}
                  subText={percentageLabel(ecodrivingResult.data.ecoIndexMonth)}
                />
              ) : (
                <StatsCardLoading />
              )}
            </PaperBox>
          </Grid>
          <Grid item sm={3}>
            <PaperBox>
              {ecodrivingResult.status === 2 ? (
                <StatsCard
                  bigIcon={
                    <StyledPrimaryUserAvatar aria-label="icon">
                      <FontAwesomeIcon icon={faChartLine} />
                    </StyledPrimaryUserAvatar>
                  }
                  statsText={t("planning.previous_month_change", "My performance difference since last month")}
                  statsValue={
                    ecodrivingResult.status === 2 ? (
                      <Percentage
                        value={ecodrivingResult.data.ecoIndexMonth - ecodrivingResult.data.ecoIndexPrevMonth}
                        maxDecimals={1}
                      />
                    ) : (
                      "-"
                    )
                  }
                  statsIconText={ecodrivingResult.status === 2 && ecodrivingResult.data?.weekLabel}
                />
              ) : (
                <StatsCardLoading />
              )}
            </PaperBox>
          </Grid>
        </Grid>
      </Container>
      <StyledTableContainer marginTop={5}>
        <Box paddingX={3} paddingY={2}>
          <Grid container direction="row" justifyContent="space-between" alignItems="center">
            <Grid item>
              <Typography variant="h2" gutterBottom>
                {t("planning.todays_trips", "Today's Trips")}
              </Typography>
              <Typography variant="paragraphLarge">
                {t("planning.explain_choose_trip_date", "Choose a day to check trip performance.")}
              </Typography>
            </Grid>
            <Grid item>
              <Date date={date} setDate={setDate} />
            </Grid>
          </Grid>
        </Box>

        <ClpTable
          columns={columns}
          rows={ecodrivingDetailsResult.status === 2 && ecodrivingDetailsResult.data}
          loadingState={ecodrivingDetailsResult.status}
        />
      </StyledTableContainer>
    </div>
  )
}

export default EcoDriving
